import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Configuración de axios para incluir cookies en las peticiones
axios.defaults.withCredentials = true;

/**
 * Obtiene un resumen de las tareas de ClickUp para un folder específico
 * @param {string} folderId - ID del folder de ClickUp
 * @param {Object} options - Opciones de consulta
 * @param {boolean} options.includeCompleted - Si debe incluir tareas completadas/cerradas
 * @returns {Promise} - Promesa con los datos del resumen
 */
export const getTasksSummary = async (folderId, options = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (options.includeCompleted) {
      queryParams.set('includeCompleted', 'true');
    }

    const queryString = queryParams.toString();
    const url = `${BACKEND_URL}/api/clickup/folders/${folderId}/tasks-summary${queryString ? `?${queryString}` : ''}`;

    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener el resumen de tareas:', error);
    throw error;
  }
};

/**
 * Obtiene las tareas de ClickUp para una lista específica, con opción de filtrar por status
 * @param {string} listId - ID de la lista de ClickUp
 * @param {string} status - Status para filtrar (opcional)
 * @param {Object} options - Opciones de consulta
 * @param {boolean} options.includeCompleted - Si debe incluir tareas completadas/cerradas
 * @returns {Promise} - Promesa con los datos de las tareas
 */
export async function getListTasks(listId, status, options = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (status) {
      queryParams.set('status', status);
    }
    if (options.includeCompleted) {
      queryParams.set('includeCompleted', 'true');
    }

    const queryString = queryParams.toString();
    const url = `${BACKEND_URL}/api/clickup/lists/${listId}/tasks${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo tareas de la lista:', error);
    throw error;
  }
}

/**
 * Obtiene los adjuntos de una tarea específica
 * @param {string} taskId - ID de la tarea de ClickUp
 * @returns {Promise} - Promesa con los datos de los adjuntos
 */
export async function getTaskAttachments(taskId) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/attachments`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo adjuntos de la tarea:', error);
    throw error;
  }
}

/**
 * Obtiene los datos de depuración de campos personalizados para una tarea específica
 * @param {string} taskId - ID de la tarea de ClickUp
 * @returns {Promise} - Promesa con los datos de depuración
 */
export async function debugTaskFields(taskId) {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/clickup/tasks/${taskId}/debug-fields`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo datos de depuración:', error);
    throw error;
  }
}

/**
 * Obtiene las opciones disponibles para un campo personalizado específico
 * @param {string} listId - ID de la lista de ClickUp 
 * @param {string} fieldId - ID del campo personalizado
 * @returns {Promise} - Promesa con los datos del campo
 */
/* 
// NOTA: Este endpoint no está disponible en la API pública de ClickUp
export async function getCustomFieldOptions(listId, fieldId) {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/clickup/lists/${listId}/fields/${fieldId}`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo opciones de campo personalizado:', error);
    throw error;
  }
}
*/ 

/**
 * Actualiza la prioridad de una tarea en ClickUp
 * @param {string} taskId - ID de la tarea
 * @param {number} priority - Valor de prioridad (1-Urgente, 2-Alta, 3-Normal, 4-Baja)
 * @returns {Promise} - Promesa con los datos actualizados de la tarea
 */
export async function updateTaskPriority(taskId, priority) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/priority`;
    const response = await axios.put(url, { priority });
    return response.data;
  } catch (error) {
    console.error('Error actualizando prioridad de la tarea:', error);
    throw error;
  }
}

/**
 * Autoriza una tarea en ClickUp (cambia su estado a "pendiente")
 * @param {string} taskId - ID de la tarea
 * @returns {Promise} - Promesa con los datos actualizados de la tarea
 */
export async function authorizeTask(taskId) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/authorize`;
    const response = await axios.put(url);
    return response.data;
  } catch (error) {
    console.error('Error autorizando la tarea:', error);
    throw error;
  }
}

/**
 * Aprueba una solicitud (cambia el estado de "Solicitado" a "Aprobado")
 * @param {string} taskId - ID de la tarea
 * @returns {Promise} - Promesa con los datos actualizados de la tarea
 */
export async function approveRequest(taskId) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/approve-request`;
    const response = await axios.put(url);
    return response.data;
  } catch (error) {
    console.error('Error aprobando la solicitud:', error);
    throw error;
  }
}

/**
 * Deniega una solicitud (cambia el estado de "Solicitado" a "Denegado" y
 * guarda el motivo en el custom field configurado en el backend).
 * @param {string} taskId - ID de la tarea
 * @param {string} reason - Motivo del rechazo (obligatorio, no vacio)
 * @returns {Promise} - Promesa con los datos actualizados de la tarea
 */
export async function denyRequest(taskId, reason) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/deny-request`;
    const response = await axios.put(url, { reason });
    return response.data;
  } catch (error) {
    console.error('Error denegando la solicitud:', error);
    throw error;
  }
}

/**
 * Confirma la recepción de una tarea en ClickUp
 * @param {string} taskId - ID de la tarea
 * @returns {Promise} - Promesa con los datos actualizados de la tarea
 */
export async function confirmReceptionTask(taskId) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/confirm-reception`;
    const response = await axios.put(url);
    return response.data;
  } catch (error) {
    console.error('Error confirmando recepción de la tarea:', error);
    throw error;
  }
}

/**
 * Obtiene la depuración completa de una tarea
 * @param {string} taskId - ID de la tarea de ClickUp
 * @returns {Promise} - Promesa con los datos de depuración completa
 */
export async function debugFullTask(taskId) {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/clickup/tasks/${taskId}/full-debug`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo datos de depuración completa:', error);
    throw error;
  }
}

/**
 * Obtiene las subtareas de una tarea específica
 * @param {string} taskId - ID de la tarea de ClickUp
 * @returns {Promise} - Promesa con los datos de las subtareas
 */
export async function getTaskSubtasks(taskId) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/subtasks`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo subtareas de la tarea:', error);
    throw error;
  }
}

/**
 * Obtiene los comentarios de una tarea específica
 * @param {string} taskId - ID de la tarea de ClickUp
 * @returns {Promise} - Promesa con los comentarios normalizados
 */
export async function getTaskComments(taskId) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/comments`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo comentarios de la tarea:', error);
    throw error;
  }
}

/**
 * Publica un comentario en una tarea de ClickUp
 * @param {string} taskId - ID de la tarea de ClickUp
 * @param {string} commentText - Texto del comentario
 * @returns {Promise} - Promesa con el comentario creado
 */
export async function postTaskComment(taskId, commentText) {
  try {
    const url = `${BACKEND_URL}/api/clickup/tasks/${taskId}/comments`;
    const response = await axios.post(url, { commentText });
    return response.data;
  } catch (error) {
    console.error('Error publicando comentario en la tarea:', error);
    throw error;
  }
} 